import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, View, Text, StyleSheet } from 'react-native';
import { StatsDataExportService } from '../../../services/stats';
import { AccountPracInfoService, mergeAccountPracInfoByRequestedTypes } from '../../../services/account/AccountPracInfoService';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import type { AdminAssessmentDto } from '../../../types/adminAssessment';
import type {
  AccountPlaDto,
  AccountPracInfoResponseDto,
  AccountPracInfoType,
  AccountSiteDto,
  DataExportFilterDto,
  DataExportResultDto,
} from '../../../types/api';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { buildDelimitedText, buildExportTable } from '../../../utils/exportTable';
import { resolveExportVars } from '../../../utils/dataExportVars';
import { canExportText, copyTextToClipboard, downloadTextFile } from '../../../web/textExport';
import { ONLINE_SITE_LABEL, ONLINE_SITE_VALUE } from './aggReportConstants';
import { getEnabledAssessmentOptions } from './enabledAssessments';
import { StatsDateInput } from './StatsDateInput';

interface DataExportViewProps {
  storage?: any;
  apiService?: any;
  accountId?: string | null;
  /** Selected variable ids per assessment id (from DataExportSetupView), sent as request vars. */
  varIdsByAssessment?: Record<string, string[]>;
  /** Preselect this assessment, e.g. after returning from Setup. */
  initialAssessmentId?: string;
  onOpenSetup: (assessmentId: string) => void;
}

const ALL_SITES_VALUE = '';

const resolveUserStore = (): any => resolveAuthContext().userStore || null;

/** The grid renders at most this many rows; CSV and Copy All always include every loaded row. */
const MAX_RENDERED_ROWS = 500;
const RECID_COL_WIDTH = 100;
const DATA_COL_WIDTH = 140;

export const DataExportView: React.FC<DataExportViewProps> = ({
  storage = null,
  apiService = null,
  accountId = null,
  varIdsByAssessment = {},
  initialAssessmentId = '',
  onOpenSetup,
}) => {
  const [plaList, setPlaList] = useState<AccountPlaDto[]>([]);
  const [assessments, setAssessments] = useState<AdminAssessmentDto[]>([]);
  // The primary button reads Export until the first pull has been made, then Refresh.
  const [hasPulled, setHasPulled] = useState(false);
  const [siteList, setSiteList] = useState<AccountSiteDto[]>([]);
  const [assessmentId, setAssessmentId] = useState(initialAssessmentId);
  const [site, setSite] = useState(ALL_SITES_VALUE);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [results, setResults] = useState<DataExportResultDto[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const runIdRef = useRef(0);

  const dataExportService = useMemo(
    () => (apiService ? new StatsDataExportService(apiService) : null),
    [apiService]
  );
  const accountPracInfoService = useMemo(
    () => (apiService ? new AccountPracInfoService(apiService) : null),
    [apiService]
  );

  // Bundled assessment content supplies display names — local, no network fetch.
  useEffect(() => {
    if (!storage) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const list = await new AdminAssessmentStorage(storage).getAvailableAssessments();
        if (!cancelled) {
          setAssessments(list);
        }
      } catch (loadError) {
        console.warn('DataExportView: failed to load assessment content', loadError);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storage]);

  // PlaList (enabled assessments) + SiteList: cache-first, then a background refresh — same
  // pattern as AggReportView.
  useEffect(() => {
    let cancelled = false;

    const applyAccountData = (data: AccountPracInfoResponseDto | null | undefined) => {
      if (!data || cancelled) {
        return;
      }
      if (data.AccountSettings) {
        setPlaList(data.AccountSettings.PlaList ?? []);
      }
      if (data.SiteList) {
        setSiteList(data.SiteList);
      }
    };

    const loadAccountData = async () => {
      if (!accountId) {
        return;
      }
      if (storage) {
        try {
          applyAccountData(await new AccountPracInfoStorage(storage).getAccountPracInfo(accountId));
        } catch (cacheError) {
          console.warn('DataExportView: failed to read cached account data', cacheError);
        }
      }
      if (!accountPracInfoService) {
        return;
      }
      try {
        const requestedTypes: AccountPracInfoType[] = ['AccountSettings', 'AccountSras'];
        const dto = await accountPracInfoService.getAccountPracInfo(
          { AccountId: accountId, Types: requestedTypes },
          { userStore: resolveUserStore(), storage }
        );
        if (cancelled) {
          return;
        }
        applyAccountData(dto);
        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const latestCached = await pracStorage.getAccountPracInfo(accountId);
          await pracStorage.saveAccountPracInfo(
            accountId,
            mergeAccountPracInfoByRequestedTypes(latestCached, dto, requestedTypes)
          );
        }
      } catch (fetchError) {
        console.warn('DataExportView: failed to refresh account data', fetchError);
      }
    };

    void loadAccountData();
    return () => {
      cancelled = true;
    };
  }, [accountId, accountPracInfoService, storage]);

  const assessmentOptions = useMemo(
    () => getEnabledAssessmentOptions(plaList, assessments),
    [plaList, assessments]
  );

  // Default to the first enabled assessment; reset if the selection is no longer enabled.
  useEffect(() => {
    // Options load asynchronously, so keep a preselected id until they arrive.
    if (assessmentOptions.length > 0 && !assessmentOptions.some((o) => o.value === assessmentId)) {
      setAssessmentId(assessmentOptions[0].value);
    }
  }, [assessmentOptions, assessmentId]);

  const siteOptions = useMemo(
    () => [
      { value: ALL_SITES_VALUE, label: 'All Sites' },
      { value: ONLINE_SITE_VALUE, label: ONLINE_SITE_LABEL },
      ...siteList
        .map((s) => ({ value: (s.SiteId || '').trim(), label: (s.SiteDName || '').trim() || s.SiteId }))
        .filter((o) => !!o.value),
    ],
    [siteList]
  );

  const table = useMemo(() => (results ? buildExportTable(results) : null), [results]);
  const hasAssessment = assessmentOptions.some((o) => o.value === assessmentId);
  const selectedAssessmentLabel = assessmentOptions.find((o) => o.value === assessmentId)?.label ?? 'export';

  const handleRefresh = useCallback(async () => {
    if (!hasAssessment) {
      return;
    }
    if (!dataExportService) {
      setError('Data export service unavailable.');
      return;
    }

    const runId = ++runIdRef.current;
    setIsLoading(true);
    setError(null);
    setMessage(null);
    setProgress(0);

    // Empty values mean "no bound" and are omitted.
    const searchFilters: DataExportFilterDto[] = [
      { searchField: 'site', searchFilter: site },
      { searchField: 'startDate', searchFilter: startDate },
      { searchField: 'endDate', searchFilter: endDate },
    ].filter((f) => f.searchFilter !== '');

    try {
      const rows = await dataExportService.getAllDataExport(
        {
          blocknum: '1',
          blocksize: '',
          accountid: accountId ?? '',
          assessmentid: assessmentId,
          searchFilters,
          vars: resolveExportVars(
            varIdsByAssessment[assessmentId] ?? [],
            assessments.find((a) => a.sourceId === assessmentId)?.aggVars ?? []
          ),
        },
        {
          userStore: resolveUserStore(),
          storage,
          onProgress: (count) => {
            if (runId === runIdRef.current) {
              setProgress(count);
            }
          },
          isCancelled: () => runId !== runIdRef.current,
        }
      );
      if (runId === runIdRef.current) {
        setResults(rows);
      }
    } catch (fetchError) {
      if (runId === runIdRef.current) {
        setError(fetchError instanceof Error ? fetchError.message : String(fetchError));
        setResults(null);
      }
    } finally {
      if (runId === runIdRef.current) {
        setIsLoading(false);
        setHasPulled(true);
      }
    }
  }, [accountId, assessmentId, dataExportService, endDate, hasAssessment, site, startDate, storage, varIdsByAssessment, assessments]);

  const flashMessage = (text: string) => {
    setMessage(text);
    setTimeout(() => setMessage((current) => (current === text ? null : current)), 3000);
  };

  const handleDownloadCsv = () => {
    if (!table) {
      return;
    }
    const safeName = selectedAssessmentLabel.replace(/[^A-Za-z0-9_-]+/g, '_');
    const stamp = new Date().toISOString().slice(0, 10);
    const ok = downloadTextFile(`${safeName}_${stamp}.csv`, buildDelimitedText(table, ','));
    flashMessage(ok ? `Downloaded ${table.rows.length} rows` : 'Download is only available in the web app');
  };

  const handleCopyAll = async () => {
    if (!table) {
      return;
    }
    const ok = await copyTextToClipboard(buildDelimitedText(table, '\t', '\n'));
    flashMessage(ok ? `Copied ${table.rows.length} rows with headers` : 'Copy failed');
  };

  const isWeb = Platform.OS === 'web';
  const webSelectStyle = { ...webControlStyle, minWidth: 150 } as any;
  const canExport = canExportText();

  const cycleSelectValue = (options: string[], current: string, onChange: (value: string) => void) => {
    const currentIndex = options.indexOf(current);
    onChange(options[currentIndex < 0 || currentIndex === options.length - 1 ? 0 : currentIndex + 1]);
  };

  const renderedRows = table ? table.rows.slice(0, MAX_RENDERED_ROWS) : [];
  const tableWidth = table ? RECID_COL_WIDTH + (table.columns.length - 1) * DATA_COL_WIDTH : 0;
  const colStyle = (index: number) => ({ width: index === 0 ? RECID_COL_WIDTH : DATA_COL_WIDTH });

  return (
    <View style={styles.panel}>
      <View style={styles.filterBarRow}>
        <View style={styles.setupBar}>
          <Text style={styles.filterLabel}>Assessment</Text>
          {isWeb ? (
              <select
                style={{ ...webSelectStyle, minWidth: 200 }}
                value={assessmentId}
                onChange={(event: any) => setAssessmentId(String(event?.target?.value ?? ''))}
                aria-label="Assessment"
              >
                {assessmentOptions.length === 0 ? <option value="">No assessments enabled</option> : null}
                {assessmentOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <Pressable
                style={[styles.dropdownBtn, { minWidth: 200 }]}
                onPress={() => cycleSelectValue(assessmentOptions.map((o) => o.value), assessmentId, setAssessmentId)}
                accessibilityRole="button"
                accessibilityLabel="Assessment"
              >
                <Text style={styles.dropdownBtnText}>{selectedAssessmentLabel === 'export' ? 'No assessments enabled' : selectedAssessmentLabel}</Text>
                <Text style={styles.dropdownBtnChevron}>▼</Text>
              </Pressable>
            )}

          <View style={styles.filterSpacer} />

          <Pressable
            style={[styles.actionButton, !hasAssessment && styles.actionButtonDisabled]}
            onPress={() => onOpenSetup(assessmentId)}
            disabled={!hasAssessment}
            accessibilityRole="button"
            accessibilityLabel="Setup"
          >
            <Text style={styles.actionButtonText}>Setup</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.filterBarRow}>
        <View style={styles.filterBar}>
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Site</Text>
            {isWeb ? (
              <select
                style={webSelectStyle}
                value={site}
                onChange={(event: any) => setSite(String(event?.target?.value ?? ALL_SITES_VALUE))}
                aria-label="Site"
              >
                {siteOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <Pressable
                style={styles.dropdownBtn}
                onPress={() => cycleSelectValue(siteOptions.map((o) => o.value), site, setSite)}
                accessibilityRole="button"
                accessibilityLabel="Site"
              >
                <Text style={styles.dropdownBtnText}>{siteOptions.find((o) => o.value === site)?.label ?? 'All Sites'}</Text>
                <Text style={styles.dropdownBtnChevron}>▼</Text>
              </Pressable>
            )}
          </View>

          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Start Date</Text>
            <StatsDateInput value={startDate} onChange={setStartDate} ariaLabel="Start Date" />
          </View>

          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>End Date</Text>
            <StatsDateInput value={endDate} onChange={setEndDate} ariaLabel="End Date" />
          </View>

          <View style={styles.filterSpacer} />

          <Pressable
            style={[styles.actionButton, (isLoading || !hasAssessment) && styles.actionButtonDisabled]}
            onPress={() => void handleRefresh()}
            disabled={isLoading || !hasAssessment}
            accessibilityRole="button"
            accessibilityLabel="Refresh"
          >
            <Text style={styles.actionButtonText}>{isLoading ? (hasPulled ? 'Refreshing…' : 'Exporting…') : hasPulled ? 'Refresh' : 'Export'}</Text>
          </Pressable>
        </View>
      </View>

      {table && canExport ? (
        <View style={styles.filterBarRow}>
          <View style={styles.filterBar}>
            <Pressable style={styles.actionButton} onPress={handleDownloadCsv} accessibilityRole="button" accessibilityLabel="Download CSV">
              <Text style={styles.actionButtonText}>Download CSV</Text>
            </Pressable>
            <View style={styles.filterSpacer} />
            <Pressable style={styles.actionButton} onPress={() => void handleCopyAll()} accessibilityRole="button" accessibilityLabel="Copy All">
              <Text style={styles.actionButtonText}>Copy All</Text>
            </Pressable>
            {message ? <Text style={styles.messageText}>{message}</Text> : null}
          </View>
        </View>
      ) : null}

      <View style={styles.mainCardRow}>
        <View style={styles.mainCard}>
          {error ? (
            <View style={styles.errorCard}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}

          {isLoading ? (
            <View style={styles.placeholderRow}>
              <Text style={styles.placeholderText}>
                {progress > 0 ? `Loaded ${progress.toLocaleString()} rows…` : 'Loading data…'}
              </Text>
            </View>
          ) : !table ? (
            <View style={styles.placeholderRow}>
              <Text style={styles.placeholderText}>
                {assessmentOptions.length === 0
                  ? 'No assessments are enabled for this practice.'
                  : 'Select an assessment and filters, then press Refresh.'}
              </Text>
            </View>
          ) : table.rows.length === 0 ? (
            <View style={styles.placeholderRow}>
              <Text style={styles.placeholderText}>No records for the selected filters.</Text>
            </View>
          ) : (
            <>
              {table.rows.length > MAX_RENDERED_ROWS ? (
                <Text style={styles.noteText}>
                  Showing first {MAX_RENDERED_ROWS} of {table.rows.length.toLocaleString()} rows; Download CSV and Copy All include all rows.
                </Text>
              ) : (
                <Text style={styles.noteText}>{table.rows.length.toLocaleString()} rows</Text>
              )}
              {/* Outer horizontal scroll carries header + body so columns stay aligned; the inner
                  vertical scroll leaves the header pinned. */}
              <ScrollView horizontal style={styles.gridScroll}>
                <View style={{ width: tableWidth }}>
                  <View style={styles.tableHeader}>
                    {table.columns.map((name, index) => (
                      <View key={`${name}-${index}`} style={[styles.tableCell, colStyle(index)]}>
                        <Text style={styles.headerText} numberOfLines={1}>{name}</Text>
                      </View>
                    ))}
                  </View>
                  <ScrollView style={styles.gridBodyScroll}>
                    {renderedRows.map((row, rowIndex) => (
                      <View key={rowIndex} style={styles.tableRow}>
                        {row.map((value, index) => (
                          <View key={index} style={[styles.tableCell, colStyle(index)]}>
                            <Text style={styles.cellText} numberOfLines={1}>{value}</Text>
                          </View>
                        ))}
                      </View>
                    ))}
                  </ScrollView>
                </View>
              </ScrollView>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    minHeight: 0,
    padding: 20,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterBarRow: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 16,
  },
  setupBar: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterBar: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
  },
  filterLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginRight: 8,
  },
  filterSpacer: {
    width: 20,
  },
  dropdownBtn: {
    height: 28,
    minWidth: 150,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
  },
  dropdownBtnChevron: {
    ...editorText,
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
    marginLeft: 4,
  },
  actionButton: {
    height: 28,
    paddingHorizontal: 16,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtonDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  actionButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  messageText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
    marginLeft: 16,
  },
  mainCardRow: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    alignItems: 'center',
  },
  mainCard: {
    width: '100%',
    flex: 1,
    minHeight: 0,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.separator,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
    overflow: 'hidden',
  },
  noteText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  gridScroll: {
    flex: 1,
    minHeight: 0,
  },
  gridBodyScroll: {
    flex: 1,
    minHeight: 0,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.panelSectionBorder,
  },
  tableCell: {
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  headerText: {
    ...editorText,
    fontWeight: '600',
    color: EDITOR_COLORS.textSecondary,
  },
  cellText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    margin: 12,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 20,
  },
  placeholderRow: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
  },
});
